import { useEffect, useState } from 'react';

import { generateTOTP, getTOTPTimeRemaining } from '../lib/totp';

export interface RecoveryData {
  url?: string;
  secret_key?: string;
  totp_secret?: string;
  [key: string]: unknown;
}

interface RecoveryDisplayProps {
  data: RecoveryData;
}

export function RecoveryDisplay({ data }: RecoveryDisplayProps): JSX.Element {
  const [totpCode, setTotpCode] = useState<string>('------');
  const [timeRemaining, setTimeRemaining] = useState<number>(30);
  const [copied, setCopied] = useState<string | null>(null);
  const [showSecretKey, setShowSecretKey] = useState<boolean>(false);

  useEffect(() => {
    if (!data.totp_secret) return;

    const updateTOTP = async () => {
      try {
        const code = await generateTOTP(data.totp_secret!);
        setTotpCode(code);
      } catch {
        setTotpCode('ERROR');
      }
    };

    const updateTimer = () => {
      setTimeRemaining(getTOTPTimeRemaining());
    };

    // Initial update
    void updateTOTP();
    updateTimer();

    // Update TOTP every second to catch rotation
    const interval = setInterval(() => {
      updateTimer();
      const remaining = getTOTPTimeRemaining();
      if (remaining === 30) {
        void updateTOTP();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [data.totp_secret]);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Failed to copy
    }
  };

  const progressPercent = (timeRemaining / 30) * 100;

  const censorSecretKey = (key: string): string => {
    const sections = key.split('-');
    if (sections.length <= 2) {
      // If there are 2 or fewer sections, show all
      return key;
    }
    // Show first and last section, censor the middle ones
    return sections
      .map((section, index) => {
        if (index === 0 || index === sections.length - 1) {
          return section;
        }
        return '•'.repeat(section.length);
      })
      .join('-');
  };

  return (
    <div className="recovery-display">
      {data.url && (
        <div className="recovery-item">
          <label>URL</label>
          <div className="value">{data.url}</div>
          <button
            type="button"
            className="copy-btn"
            onClick={() => copyToClipboard(data.url!, 'url')}
          >
            {copied === 'url' ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      )}

      {data.secret_key && (
        <div className="recovery-item">
          <label>Secret Key</label>
          <div className="secret-key-wrapper">
            <div className="value mono">
              {showSecretKey ? data.secret_key : censorSecretKey(data.secret_key)}
            </div>
            <button
              type="button"
              className="eye-btn"
              onClick={() => setShowSecretKey(!showSecretKey)}
              aria-label={showSecretKey ? 'Hide secret key' : 'Show secret key'}
            >
              {showSecretKey ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          <button
            type="button"
            className="copy-btn"
            onClick={() => copyToClipboard(data.secret_key!, 'secret')}
          >
            {copied === 'secret' ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      )}

      {data.totp_secret && (
        <div className="recovery-item totp-item">
          <label>TOTP Code</label>
          <div className="totp-wrapper">
            <div className="totp-code">{totpCode}</div>
            <svg className="totp-ring" width="48" height="48" viewBox="0 0 48 48">
              <circle
                className="ring-background"
                cx="24"
                cy="24"
                r="20"
                fill="none"
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth="3"
              />
              <circle
                className="ring-progress"
                cx="24"
                cy="24"
                r="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 20}`}
                strokeDashoffset={`${2 * Math.PI * 20 * (1 - progressPercent / 100)}`}
                transform="rotate(-90 24 24)"
              />
              <text
                x="24"
                y="24"
                textAnchor="middle"
                dominantBaseline="middle"
                className="ring-text"
              >
                {timeRemaining}
              </text>
            </svg>
          </div>
          <button
            type="button"
            className="copy-btn"
            onClick={() => copyToClipboard(totpCode, 'totp')}
          >
            {copied === 'totp' ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      )}
    </div>
  );
}
